import { useState, useEffect } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { CheckSuccessIcon, CheckFailureIcon, ErrorIcon } from "./ClosureIcons";
import { RadioButtonUnchecked } from "@mui/icons-material";
import { waitFn } from "../../../../utils/settings";

const ClosureChecks = ({
  checks,
  currentCheckIndex,
  setCurrentCheckIndex,
  checksStatusData,
}: {
  checks: { label: string; key: string; errorMessage: string }[];
  currentCheckIndex: number;
  setCurrentCheckIndex: React.Dispatch<React.SetStateAction<number>>;
  checksStatusData: { [key: string]: string };
}) => {
  const [checkStatuses, setCheckStatuses] = useState<{ [key: string]: string }>(
    {}
  );
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    const verifyCheck = async () => {
      const check = checks[currentCheckIndex];
      setCheckStatuses((prev) => ({ ...prev, [check.key]: "loading" }));
      await waitFn();
      if (!checksStatusData[check.key]) {
        setCheckStatuses((prev) => ({ ...prev, [check.key]: "failure" }));
        setErrorMsg(check.errorMessage);
      } else {
        setCheckStatuses((prev) => ({ ...prev, [check.key]: "success" }));
        setCurrentCheckIndex((prev) => prev + 1);
      }
    };

    if (checksStatusData && currentCheckIndex < checks.length) {
      verifyCheck();
    }
  }, [currentCheckIndex, checks, setCurrentCheckIndex, checksStatusData]);

  return (
    <>
      <Stack gap={2}>
        {checks.map((item) => {
          const status = checkStatuses[item.key] || "pending";

          return (
            <Box
              key={item.key}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Box sx={{ width: 24, display: "flex", alignItems: "center" }}>
                {status === "loading" && <CircularProgress size={22} />}
                {status === "success" && <CheckSuccessIcon />}
                {status === "failure" && <CheckFailureIcon />}
                {status === "pending" && (
                  <RadioButtonUnchecked sx={{ color: "#9CA3AF" }} />
                )}
              </Box>
              <Typography variant="subtitle1" fontWeight="regular">
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      {errorMsg && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            borderRadius: "6px",
            bgcolor: "#FF71640A",
            py: "6px",
            pl: "20px",
            pr: 5,
            mt: 5,
            minHeight: "40px",
            width: "fit-content",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "5px",
              backgroundColor: "#FF7164",
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
            },
          }}
        >
          <ErrorIcon />
          <Typography variant="body1" sx={{ color: "#FF7164" }}>
            {errorMsg}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default ClosureChecks;
